<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { useRoute, useRouter, type RouteLocationNormalized } from 'vue-router';
import { routerTabMessages } from '../../messages';
import { useRouterTab, type TabItem } from '../router/useRouterTab';
import { useSidebarLayout } from './sidebarLayout';

const props = withDefaults(
  defineProps<{
    homeRoute?: string;
    homeTitle?: string;
    homeIcon?: string;
  }>(),
  {
    homeRoute: '/',
    homeTitle: 'Dashboard',
  },
);

const route = useRoute();
const router = useRouter();

const {
  tabs,
  addTab,
  closeTab,
  closeOtherTabs,
  closeAllTabs,
  closeLeftTabs,
  closeRightTabs,
  hasClosableLeftOf,
  hasClosableRightOf,
  refreshTab,
  refreshAllTabs,
  reorderTabs,
  updateTabTitle,
  updateTabIcon,
} = useRouterTab({
  homeRoute: props.homeRoute,
  homeTitle: props.homeTitle,
  homeIcon: props.homeIcon,
});

const { collapsed } = useSidebarLayout();

watch(
  () => route.fullPath,
  () => {
    addTab();
  },
  { immediate: true },
);

const onRouteTabUpdate = (event: Event) => {
  const detail = (event as CustomEvent).detail;
  if (!detail?.path) {
    return;
  }

  if (detail.title) {
    updateTabTitle(detail.path, detail.title);
  }
  if (detail.icon) {
    updateTabIcon(detail.path, detail.icon);
  }
};

const isActive = (fullPath: string) => route.fullPath === fullPath;

const navigateTo = (fullPath: string) => {
  router.push(fullPath);
};

const draggedTab = ref<TabItem | null>(null);
const dragOverIndex = ref<number | null>(null);

const onDragStart = (event: DragEvent, tab: TabItem, _index: number) => {
  if (!tab.closable) {
    event.preventDefault();
    return;
  }
  draggedTab.value = tab;
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', tab.fullPath);
  }
};

const onDragOver = (event: DragEvent, index: number) => {
  event.preventDefault();
  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = 'move';
  }
  dragOverIndex.value = index;
};

const onDragLeave = () => {
  dragOverIndex.value = null;
};

const onDrop = (event: DragEvent, targetIndex: number) => {
  event.preventDefault();
  if (!draggedTab.value) {
    return;
  }

  const fromIndex = tabs.value.findIndex(
    (t) => t.fullPath === draggedTab.value?.fullPath,
  );
  if (fromIndex !== -1 && fromIndex !== targetIndex) {
    reorderTabs(fromIndex, targetIndex);
  }

  draggedTab.value = null;
  dragOverIndex.value = null;
};

const onDragEnd = () => {
  draggedTab.value = null;
  dragOverIndex.value = null;
};

const contextMenuEl = ref<HTMLElement | null>(null);
const contextMenu = ref<{
  show: boolean;
  x: number;
  y: number;
  tab: TabItem | null;
}>({
  show: false,
  x: 0,
  y: 0,
  tab: null,
});

const canCloseLeft = computed(() => {
  const tab = contextMenu.value.tab;
  return !!tab && hasClosableLeftOf(tab.fullPath);
});

const canCloseRight = computed(() => {
  const tab = contextMenu.value.tab;
  return !!tab && hasClosableRightOf(tab.fullPath);
});

const onContextMenu = async (event: MouseEvent, tab: TabItem) => {
  event.preventDefault();

  contextMenu.value = {
    show: true,
    x: event.clientX,
    y: event.clientY,
    tab,
  };

  await nextTick();

  if (contextMenuEl.value) {
    const menuRect = contextMenuEl.value.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    if (contextMenu.value.x + menuRect.width > viewportWidth) {
      contextMenu.value.x = viewportWidth - menuRect.width - 8;
    }

    if (contextMenu.value.y + menuRect.height > viewportHeight) {
      contextMenu.value.y = viewportHeight - menuRect.height - 8;
    }
  }
};

const closeContextMenu = () => {
  contextMenu.value.show = false;
  contextMenu.value.tab = null;
};

const handleContextAction = async (action: string) => {
  const tab = contextMenu.value.tab;
  if (!tab) {
    return;
  }

  switch (action) {
    case 'close':
      closeTab(tab.fullPath);
      break;
    case 'closeOthers':
      closeOtherTabs(tab.fullPath);
      break;
    case 'closeAll':
      closeAllTabs();
      break;
    case 'closeLeft':
      closeLeftTabs(tab.fullPath);
      break;
    case 'closeRight':
      closeRightTabs(tab.fullPath);
      break;
    case 'refresh':
      await refreshTab(tab.fullPath);
      break;
    case 'refreshAll':
      await refreshAllTabs();
      break;
  }
  closeContextMenu();
};

onMounted(() => {
  document.addEventListener('click', closeContextMenu);
  window.addEventListener('itp:routeTab', onRouteTabUpdate);
});

onUnmounted(() => {
  document.removeEventListener('click', closeContextMenu);
  window.removeEventListener('itp:routeTab', onRouteTabUpdate);
});
</script>

<template>
  <div class="router-tab-sidebar" :class="{ 'is-collapsed': collapsed }">
    <div
      v-for="(tab, index) in tabs"
      :key="tab.id"
      class="router-tab-sidebar__item"
      :class="{
        'is-active': isActive(tab.fullPath),
        'is-closable': tab.closable,
        'is-drag-over': dragOverIndex === index,
      }"
      :title="tab.title"
      :draggable="tab.closable"
      @click="navigateTo(tab.fullPath)"
      @contextmenu="onContextMenu($event, tab)"
      @dragstart="onDragStart($event, tab, index)"
      @dragover="onDragOver($event, index)"
      @dragleave="onDragLeave"
      @drop="onDrop($event, index)"
      @dragend="onDragEnd"
    >
      <ItpIcon
        v-if="tab.icon"
        :name="tab.icon"
        class="router-tab-sidebar__item-icon"
      />
      <span class="router-tab-sidebar__item-title">
        {{ tab.title }}
      </span>
      <i
        v-if="tab.closable"
        class="router-tab-sidebar__item-close"
        @click.prevent.stop="closeTab(tab.fullPath)"
      />
    </div>

    <Teleport to="body">
      <div
        v-if="contextMenu.show"
        ref="contextMenuEl"
        class="router-tab-context-menu"
        :style="{ left: contextMenu.x + 'px', top: contextMenu.y + 'px' }"
      >
        <div
          v-if="contextMenu.tab?.closable"
          class="context-menu-item"
          @click="handleContextAction('close')"
        >
          {{ routerTabMessages.close }}
        </div>
        <div
          v-if="canCloseLeft"
          class="context-menu-item"
          @click="handleContextAction('closeLeft')"
        >
          {{ routerTabMessages.closeAbove }}
        </div>
        <div
          v-if="canCloseRight"
          class="context-menu-item"
          @click="handleContextAction('closeRight')"
        >
          {{ routerTabMessages.closeBelow }}
        </div>
        <div
          class="context-menu-item"
          @click="handleContextAction('closeOthers')"
        >
          {{ routerTabMessages.closeOthers }}
        </div>
        <div class="context-menu-item" @click="handleContextAction('closeAll')">
          {{ routerTabMessages.closeAll }}
        </div>
        <div class="context-menu-divider"></div>
        <div class="context-menu-item" @click="handleContextAction('refresh')">
          {{ routerTabMessages.refresh }}
        </div>
        <div
          class="context-menu-item"
          @click="handleContextAction('refreshAll')"
        >
          {{ routerTabMessages.refreshAll }}
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style lang="scss" scoped>
.router-tab-sidebar {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 0.25rem;
  width: 100%;
  min-width: 0;
}

.router-tab-sidebar__item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  color: #6c757d;
  border-radius: 0.375rem;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  border: 1px solid transparent;
  transition:
    color 0.15s ease,
    background-color 0.15s ease;
  min-width: 0;

  &:hover:not(.is-active) {
    color: #495057;
    background-color: rgba(0, 0, 0, 0.04);
  }

  &.is-active {
    color: var(--bs-primary, #0d6efd);
    font-weight: 600;
    background-color: rgba(13, 110, 253, 0.1);
  }

  &.is-drag-over {
    background-color: rgba(13, 110, 253, 0.06);
    &::before {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      top: 0;
      height: 2px;
      background-color: var(--bs-primary, #0d6efd);
      border-radius: 1px;
    }
  }
}

.router-tab-sidebar__item-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 1.25rem;
  height: 1.25rem;
}

.router-tab-sidebar__item-title {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.router-tab-sidebar__item-close {
  position: relative;
  display: inline-block;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  flex-shrink: 0;
  opacity: 0;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    opacity 0.15s ease;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 8px;
    height: 1px;
    background-color: #6c757d;
  }

  &::before {
    transform: translate(-50%, -50%) rotate(-45deg);
  }
  &::after {
    transform: translate(-50%, -50%) rotate(45deg);
  }

  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
    &::before,
    &::after {
      background-color: #000;
    }
  }
}

.router-tab-sidebar__item:hover .router-tab-sidebar__item-close,
.router-tab-sidebar__item.is-active .router-tab-sidebar__item-close {
  opacity: 0.5;
}
.router-tab-sidebar__item .router-tab-sidebar__item-close:hover {
  opacity: 1;
}

.router-tab-sidebar.is-collapsed {
  padding: 0.25rem 0;

  .router-tab-sidebar__item {
    justify-content: center;
    padding: 0.5rem 0;
    gap: 0;
  }

  .router-tab-sidebar__item-title,
  .router-tab-sidebar__item-close {
    display: none;
  }

  .router-tab-sidebar__item-icon {
    width: 1.5rem;
    height: 1.5rem;
  }
}
</style>
